import * as React from 'react';
import { ContentState, Modifier, SelectionState } from 'draft-js';
import { EditIcon, X } from 'lucide-react';
import { CTAButtonEntityData } from '../StyleControls/types';
import { Button } from '@/shadcn/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shadcn/components/ui/tooltip';
import { CTAButtonConfigModal, CTAButtonConfig } from '../../MessageComposer/EmailEditor/CTAButton';

interface IProps {
  contentState: ContentState;
  entityKey: string;
  blockKey: string;
  updateEntityData?: (newContentState: ContentState, changeType?: string) => void;
  setEditorDisabled?: (disabled: boolean) => void;
}

export const CTAButtonBlock: React.FC<IProps> = (props) => {
  const { contentState, entityKey, blockKey, updateEntityData, setEditorDisabled } = props;
  const entity = contentState.getEntity(entityKey);
  const data = entity.getData() as { data: CTAButtonEntityData };
  const ctaData = data.data;

  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (ctaData.url) {
      window.open(ctaData.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditModalOpen(true);
    // Disable the editor to prevent focus conflicts
    if (setEditorDisabled) {
      setEditorDisabled(true);
    }
  };

  const handleDelete = React.useCallback(() => {
    if (!updateEntityData) return;

    // Find the block that contains this entity
    const block = contentState.getBlockForKey(blockKey);
    if (!block) return;

    // Create a selection for the entire block
    const blockSelection = SelectionState.createEmpty(blockKey).merge({
      anchorOffset: 0,
      focusOffset: block.getLength(),
    });

    // Remove the entire block containing the CTA button
    const newContentState = Modifier.removeRange(
      contentState,
      blockSelection,
      'backward'
    );

    updateEntityData(newContentState, 'remove-range');
  }, [contentState, updateEntityData, blockKey]);

  const handleClose = () => {
    setIsEditModalOpen(false);
    // Re-enable the editor when modal closes
    if (setEditorDisabled) {
      setEditorDisabled(false);
    }
  };

  const handleSave = (config: CTAButtonConfig) => {
    if (!updateEntityData) return;

    const newCtaData: CTAButtonEntityData = {
      text: config.text,
      url: config.url,
      backgroundColor: config.backgroundColor,
      textColor: config.textColor,
      alignment: config.alignment,
    };

    // Use mergeEntityData to update only the entity data without affecting block text
    const newContentState = contentState.mergeEntityData(entityKey, { data: newCtaData });

    // Use a more specific change type that won't affect block text
    updateEntityData(newContentState, 'apply-entity');
    handleClose();
  };

  const alignment = ctaData.alignment || 'center';
  const justifyClass = alignment === 'left'
    ? 'justify-start'
    : alignment === 'right'
    ? 'justify-end'
    : 'justify-center';

  // Memoize the initial config to prevent unnecessary re-renders and resets
  const memoizedInitialConfig = React.useMemo(() => ({
    text: ctaData.text,
    url: ctaData.url,
    backgroundColor: ctaData.backgroundColor,
    textColor: ctaData.textColor,
    alignment: ctaData.alignment,
  }), [ctaData.text, ctaData.url, ctaData.backgroundColor, ctaData.textColor, ctaData.alignment]);

  return (
    <div className={`flex ${justifyClass} my-4 relative group`} contentEditable={false}>
      <button
        type="button"
        className="inline-flex items-center justify-center px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-opacity-50 cursor-pointer"
        style={{
          backgroundColor: ctaData.backgroundColor,
          color: ctaData.textColor,
          border: `1px solid ${ctaData.backgroundColor}`,
        }}
        onClick={handleClick}
      >
        {ctaData.text}
      </button>

      {/* Controls overlay */}
      <div className="absolute top-0 right-0 flex gap-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 transform -translate-y-2 translate-x-2">
        {/* Edit button */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="secondary"
                className="bg-white/90 backdrop-blur border border-black/10 shadow-md hover:bg-white hover:border-blue-400 text-gray-600 hover:text-blue-600 min-w-[28px] min-h-[28px] flex items-center justify-center"
                onClick={handleEdit}
              >
                <EditIcon width={12} height={12} className="text-current" style={{ color: 'currentColor' }} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Edit CTA button</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Delete button */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="destructive"
                className="bg-white/90 backdrop-blur border border-black/10 shadow-md hover:bg-white hover:border-red-400 text-red-600 hover:text-red-700 min-w-[28px] min-h-[28px] flex items-center justify-center"
                onClick={handleDelete}
              >
                <X width={12} height={12} className="text-current" style={{ color: 'currentColor' }} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Delete CTA button</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Edit Modal */}
      <CTAButtonConfigModal
        isOpen={isEditModalOpen}
        onClose={handleClose}
        onSave={handleSave}
        initialConfig={memoizedInitialConfig}
      />
    </div>
  );
};

CTAButtonBlock.displayName = 'CTAButtonBlock';
